import React, {
  useState,
  useRef,
  useEffect,
  useMemo,
  memo,
} from "react"
import { useStudioActions } from "../../hooks/useStudioActions"
import {
  FiUploadCloud,
  FiMessageSquare,
  FiCpu,
  FiFileText,
  FiSend,
  FiLoader,
  FiTerminal,
  FiX,
} from "react-icons/fi"
import { useNotification } from "../../context/NotificationContext"

interface ChatMessage {
  role: "user" | "assistant"
  content: string
}

const MessageBubble = memo(
  ({ msg }: { msg: ChatMessage }) => (
    <div
      className={`d-flex ${msg.role === "user"
          ? "justify-content-end"
          : "justify-content-start"
        } animate-fade-in-up`}
    >
      <div
        className={`max-w-75 p-3 rounded-4 shadow-sm ${msg.role === "user"
            ? "bg-primary text-white"
            : "bg-white text-dark border border-light"
          }`}
      >
        <div className="x-small fw-900 mb-1 uppercase tracking-widest opacity-60">
          {msg.role === "user" ? "YOU" : "AI ENGINE"}
        </div>

        <div
          className="fw-medium lh-lg"
          style={{ fontSize: "0.95rem" }}
        >
          {msg.content}
        </div>
      </div>
    </div>
  )
)

MessageBubble.displayName = "MessageBubble"

export default function DocumentStudio() {
  const [file, setFile] = useState<File | null>(null)
  const [analysis, setAnalysis] = useState<string>("")
  const [loading, setLoading] = useState<boolean>(false)
  const [chatLoading, setChatLoading] =
    useState<boolean>(false)
  const [messages, setMessages] = useState<ChatMessage[]>(
    []
  )
  const [input, setInput] = useState("")

  const docContextRef = useRef("")
  const chatEndRef = useRef<HTMLDivElement>(null)

  const { analyzeDoc, chatWithDoc } =
    useStudioActions()

  const { showNotification } = useNotification()

  const scrollToBottom = () => {
    chatEndRef.current?.scrollIntoView({
      behavior: "smooth",
    })
  }

  useEffect(() => {
    requestAnimationFrame(() => {
      scrollToBottom()
    })
  }, [messages.length])

  const renderedAnalysis = useMemo(() => {
    return analysis.split("\n").map((line, i) => (
      <p
        key={i}
        className={
          line.startsWith("#") ||
            line.includes("**")
            ? "text-dark fw-900 fs-5 mt-4 mb-2"
            : "mb-2 fw-medium"
        }
      >
        {line.replace(/[#*]/g, "")}
      </p>
    ))
  }, [analysis])

  const handleUpload = async (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const selectedFile = e.target.files?.[0]

    if (!selectedFile) return

    setFile(selectedFile)

    setLoading(true)
    setAnalysis("")
    setMessages([])

    const formData = new FormData()

    formData.append("file", selectedFile)

    try {
      const data = await analyzeDoc(formData)

      setAnalysis(data.result)

      docContextRef.current = data.content

      showNotification(
        "Document analyzed! You can now chat with it.",
        "success"
      )

      setMessages([
        {
          role: "assistant",
          content: `I've analyzed "${selectedFile.name}". I'm ready to help you with any questions. You can ask me to summarize sections, explain concepts, or extract information.`,
        },
      ])
    } catch (error: any) {
      showNotification(
        error.response?.data?.error ||
        "Analysis failed.",
        "error"
      )
    } finally {
      setLoading(false)
    }
  }

  const handleChat = async (
    e: React.FormEvent
  ) => {
    e.preventDefault()

    if (
      !input.trim() ||
      chatLoading ||
      !docContextRef.current
    )
      return

    const userMessage = input.trim()

    setInput("")

    setMessages((prev) => [
      ...prev,
      {
        role: "user",
        content: userMessage,
      },
    ])

    setChatLoading(true)

    try {
      const history = messages.slice(-5)

      const data = await chatWithDoc(
        userMessage,
        docContextRef.current,
        history
      )

      setMessages((prev) => [
        ...prev,
        {
          role: "assistant",
          content: data.reply,
        },
      ])
    } catch (error: any) {
      showNotification(
        "Failed to get response.",
        "error"
      )
    } finally {
      setChatLoading(false)
    }
  }

  return (
    <div className="min-vh-100 hero-bg overflow-hidden position-relative pt-5">
      {/* Optimized Background Blobs */}
      <div
        className="blob bg-primary"
        style={{
          top: "-10%",
          left: "-5%",
          width: "500px",
          height: "500px",
          opacity: 0.12,
        }}
      ></div>

      <div
        className="blob bg-secondary"
        style={{
          bottom: "10%",
          right: "-5%",
          width: "450px",
          height: "450px",
          opacity: 0.08,
        }}
      ></div>

      <div className="container-fluid px-4 pt-5 h-100 position-relative z-10">
        {/* Header */}
        <div className="text-center mb-5 animate-fade-in-up">
          <div className="badge glass rounded-pill px-4 py-2 text-primary fw-900 mb-3 tracking-wider shadow-sm">
            VISION STUDIO
          </div>

          <h2 className="display-4 fw-900 mb-2 tracking-tight text-dark">
            Neural{" "}
            <span className="text-gradient-premium">
              Intelligence
            </span>{" "}
            Hub
          </h2>

          <p className="text-muted fw-medium fs-5">
            Upload, Analyze, and Interact with your
            documents in real-time.
          </p>
        </div>

        <div className="row g-4 h-100 pb-5">
          {/* LEFT PANEL */}
          <div className="col-lg-5 d-flex flex-column">
            <div
              className="card-premium glass overflow-hidden d-flex flex-column p-4 border-0"
              style={{
                height: "82vh",
                minHeight: "500px",
              }}
            >
              {/* Header */}
              <div className="d-flex justify-content-between align-items-center mb-4 flex-shrink-0">
                <div className="d-flex align-items-center gap-3">
                  <div
                    className="icon-box mb-0 shadow-glow"
                    style={{
                      width: "50px",
                      height: "50px",
                      borderRadius: "15px",
                    }}
                  >
                    <FiTerminal size={24} />
                  </div>

                  <div>
                    <h4 className="text-dark fw-900 mb-0">
                      Parser Core
                    </h4>

                    <span className="x-small text-primary fw-bold tracking-widest uppercase">
                      Active Analysis
                    </span>
                  </div>
                </div>

                {file && (
                  <button
                    className="btn btn-sm glass-dark rounded-pill px-3 py-2 text-danger border-0 smooth-transition"
                    onClick={() => {
                      setFile(null)
                      setAnalysis("")
                      setMessages([])
                      docContextRef.current = ""
                    }}
                  >
                    <FiX />
                    {" "}Reset
                  </button>
                )}
              </div>

              {!file ? (
                <div
                  className="flex-grow-1 d-flex flex-column align-items-center justify-content-center border-2 border-dashed border-primary border-opacity-25 rounded-5 smooth-transition cursor-pointer bg-light bg-opacity-50 hover-scale"
                  onClick={() =>
                    document
                      .getElementById(
                        "studio-upload"
                      )
                      ?.click()
                  }
                >
                  <div className="mb-4">
                    <FiUploadCloud
                      size={100}
                      className="text-primary opacity-30"
                    />
                  </div>

                  <h3 className="text-dark fw-900">
                    Drop Knowledge Base
                  </h3>

                  <p className="text-muted fw-bold">
                    PDF, TXT, DOCX • MAX 20MB
                  </p>

                  <input
                    id="studio-upload"
                    type="file"
                    className="d-none"
                    onChange={handleUpload}
                  />
                </div>
              ) : (
                <div className="flex-grow-1 d-flex flex-column overflow-hidden">
                  {/* File Card */}
                  <div className="glass rounded-4 p-3 mb-4 d-flex align-items-center gap-3 border-0 shadow-sm bg-white bg-opacity-50 flex-shrink-0">
                    <div className="bg-primary bg-opacity-10 p-2 rounded-3 text-primary">
                      <FiFileText size={30} />
                    </div>

                    <div className="overflow-hidden">
                      <div className="text-dark fw-900 text-truncate">
                        {file.name}
                      </div>

                      <div className="x-small text-muted fw-bold uppercase">
                        Semantic Link Established
                      </div>
                    </div>
                  </div>

                  {/* Analysis */}
                  <div className="flex-grow-1 custom-scrollbar overflow-auto pe-2">
                    {loading ? (
                      <div className="h-100 d-flex flex-column align-items-center justify-content-center text-center py-5">
                        <FiLoader
                          className="spin text-primary mb-3"
                          size={48}
                        />

                        <h5 className="text-dark fw-900">
                          Decoding Neural
                          Pathways...
                        </h5>

                        <div
                          className="progress w-75 bg-light rounded-pill overflow-hidden mt-3"
                          style={{ height: "6px" }}
                        >
                          <div
                            className="progress-bar progress-bar-striped progress-bar-animated bg-primary"
                            style={{ width: "100%" }}
                          ></div>
                        </div>
                      </div>
                    ) : (
                      <div className="analysis-text text-muted pb-4">
                        {renderedAnalysis}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* RIGHT PANEL */}
          <div className="col-lg-7 d-flex flex-column">
            <div
              className="card-premium glass overflow-hidden d-flex flex-column border-0"
              style={{
                height: "82vh",
                minHeight: "500px",
              }}
            >
              {/* Header */}
              <div className="p-4 border-bottom border-light d-flex justify-content-between align-items-center bg-white bg-opacity-40 flex-shrink-0">
                <div className="d-flex align-items-center gap-3">
                  <div className="p-2 rounded-3 bg-success bg-opacity-10 text-success shadow-sm">
                    <FiMessageSquare size={24} />
                  </div>

                  <h5 className="text-dark fw-900 mb-0">
                    AI Assistant
                  </h5>
                </div>

                <div className="d-none d-md-flex gap-2">
                  {[
                    "Summary",
                    "Data Extractor",
                    "Insights",
                  ].map((tag) => (
                    <span
                      key={tag}
                      className="badge bg-light rounded-pill px-3 py-2 text-muted smooth-transition border border-secondary-subtle x-small fw-bold"
                    >
                      {tag}
                    </span>
                  ))}
                </div>
              </div>

              {/* Chat */}
              <div
                className="flex-grow-1 overflow-auto p-4 custom-scrollbar d-flex flex-column gap-4"
                style={{
                  background:
                    "rgba(248, 250, 252, 0.5)",
                }}
              >
                {messages.length === 0 ? (
                  <div className="h-100 d-flex flex-column align-items-center justify-content-center text-center opacity-40 py-5">
                    <FiCpu
                      size={80}
                      className="mb-4 text-primary"
                    />

                    <h4 className="text-dark fw-900">
                      Interactive Interface
                    </h4>

                    <p className="text-muted fw-medium max-w-sm">
                      Establish a neural link by uploading
                      a document.
                    </p>
                  </div>
                ) : (
                  <>
                    {messages.map((msg, i) => (
                      <MessageBubble
                        key={i}
                        msg={msg}
                      />
                    ))}

                    {chatLoading && (
                      <div className="d-flex justify-content-start animate-fade-in-up">
                        <div className="bg-white p-3 rounded-4 border border-light shadow-sm">
                          <div className="d-flex gap-1 align-items-center">
                            <span className="small fw-900 text-primary">
                              Thinking...
                            </span>
                          </div>
                        </div>
                      </div>
                    )}

                    <div ref={chatEndRef} />
                  </>
                )}
              </div>

              {/* Input */}
              <div className="p-4 bg-white bg-opacity-60 border-top border-light flex-shrink-0">
                <form
                  onSubmit={handleChat}
                  className="position-relative"
                >
                  <input
                    type="text"
                    className="form-control glass border-secondary-subtle rounded-pill py-3 px-4 text-dark shadow-none"
                    placeholder={
                      file
                        ? "Ask anything about the content..."
                        : "Upload a file first..."
                    }
                    value={input}
                    onChange={(e) =>
                      setInput(e.target.value)
                    }
                    disabled={
                      !file || chatLoading
                    }
                    style={{
                      background: "white",
                    }}
                  />

                  <button
                    type="submit"
                    className="btn btn-primary rounded-circle p-2 position-absolute top-50 end-0 translate-middle-y me-2 shadow-glow border-0 smooth-transition d-flex align-items-center justify-content-center"
                    disabled={
                      !file ||
                      chatLoading ||
                      !input.trim()
                    }
                    style={{
                      width: "42px",
                      height: "42px",
                    }}
                  >
                    <FiSend size={20} />
                  </button>
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Optimized Styles */}
      <style>{`
        .analysis-text {
          font-family: 'Inter', sans-serif;
          line-height: 1.8;
          font-size: 0.95rem;
        }

        .max-w-75 {
          max-width: 75%;
        }

        .x-small {
          font-size: 0.65rem;
        }

        .uppercase {
          text-transform: uppercase;
        }

        .tracking-widest {
          letter-spacing: 0.1em;
        }

        .smooth-transition {
          transition:
            transform 0.2s ease,
            opacity 0.2s ease,
            background-color 0.2s ease;
        }

        .glass {
          backdrop-filter: blur(10px);
          -webkit-backdrop-filter: blur(10px);
        }

        .glass-dark {
          backdrop-filter: blur(8px);
          -webkit-backdrop-filter: blur(8px);
        }

        .hover-scale:hover {
          transform: scale(1.01);
        }

        .spin {
          animation: spin 1s linear infinite;
        }

        @keyframes spin {
          from {
            transform: rotate(0deg);
          }
          to {
            transform: rotate(360deg);
          }
        }

        .custom-scrollbar::-webkit-scrollbar {
          width: 6px;
        }

        .custom-scrollbar::-webkit-scrollbar-track {
          background: transparent;
        }

        .custom-scrollbar::-webkit-scrollbar-thumb {
          background: rgba(0,0,0,0.15);
          border-radius: 20px;
        }

        .custom-scrollbar::-webkit-scrollbar-thumb:hover {
          background: rgba(0,0,0,0.25);
        }

        @media (max-width: 768px) {
          .max-w-75 {
            max-width: 90%;
          }
        }
      `}</style>
    </div>
  )
}